import { z } from 'zod';
const combinesWith = z.object({
    orderDiscounts: z.boolean(),
    productDiscounts: z.boolean(),
    shippingDiscounts: z.boolean(),
});
const percentage = z.object({
    percentage: z.number().min(0).max(100),
});
const amount = z.object({
    amount: z.string(), // Shopify uses string for money amounts
});
const minimumRequirement = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('quantity'),
        quantity: z.object({
            greaterThanOrEqualToQuantity: z.string(),
        }),
    }),
    z.object({
        kind: z.literal('subtotal'),
        subtotal: z.object({
            greaterThanOrEqualToSubtotal: z.string(),
        }),
    }),
]);
// Shopify rule: `discountAmount.appliesOnEachItem` must be `false` when no
// specific items are scoped (i.e. when `items: { all: true }`). So the
// fixed-amount variant below locks `appliesOnEachItem` to `false` for the
// order-wide kind.
export const amountOffOrderRaw = z.object({
    kind: z.literal('amountOffOrder'),
    combinesWith,
    minimumRequirement: minimumRequirement.optional(),
    customerGets: z.object({
        value: z.union([
            percentage,
            amount.extend({ appliesOnEachItem: z.literal(false) }),
        ]),
    }),
});
// `amountOffProduct` requires a scoped items selection — matching Shopify's
// admin, where picking this discount kind forces the merchant to choose
// collections (or products). Order-wide discounts are modeled via
// `amountOffOrderRaw` instead.
//
// `collections.add` holds Corso `StoreProductCollection.id`s (ints); the
// discount-create path resolves them to Shopify `gid://shopify/Collection/*`
// via the StoreProductCollection table.
//
// TODO: add `products: { productsToAdd: GID[], productVariantsToAdd: GID[] }`
// as an alternate items shape when we expose product-level scoping.
export const amountOffProductRaw = z.object({
    kind: z.literal('amountOffProduct'),
    combinesWith,
    customerGets: z.object({
        items: z.object({
            collections: z.object({
                add: z
                    .array(z.number().int().positive())
                    .min(1, 'Select at least one collection.'),
            }),
        }),
        value: z.union([
            percentage,
            // `appliesOnEachItem: true` is valid here because items is always scoped.
            amount.extend({ appliesOnEachItem: z.boolean() }),
        ]),
    }),
    minimumRequirement: minimumRequirement.optional(),
});
// TODO: This can be expanded to a discriminated union if we add more discount types in the future
// (e.g. `discountCodeBxgyCreate` for Buy X Get Y, `discountCodeFreeShippingCreate` for free shipping).
export const discountDefinition = z.object({
    mutation: z
        .literal('discountCodeBasicCreate')
        .default('discountCodeBasicCreate'),
    parameters: z.discriminatedUnion('kind', [
        amountOffOrderRaw,
        amountOffProductRaw,
    ]),
});
export const storeDiscountDefinition = z.object({
    id: z.number(),
    title: z.string(),
    discountDefinition,
});
//# sourceMappingURL=storeDiscountDefinition.js.map